const Backbone = require('backbone');
import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import { Constants } from './Constants';
import { Dispatcher as BaseDispatcher } from './BaseDispatcher';
import { I, IPayload, check as checkAction } from '../../../pdc-platform/src/ts/actions/AppActions';

import { DEFAULT_MIN_CACHE_AGE, DEFAULT_SQL_TIME_OUT_PERIOD } from './AqlUtility';

import { useState, useEffect, DependencyList } from 'react';

const STORAGE_STORE_PREFIX = 'store-';

export interface IBaseStore {
    addChangeListener(callback: Function): void;
    removeChangeListener(callback: Function): void;
}

interface IDataCacheRecord {
    value?: any;
    deferred?: JQuery.Deferred<any>;
    selfDestructTimer?: NodeJS.Timer;
}

let storeID = 0;

export default class BaseStore<StateType, ActionsType, DispatcherType extends BaseDispatcher, Glob extends IGlob>
    extends Backbone.Model
    implements IBaseStore
{
    static CHANGE_EVENT: string = 'change';

    public _state: StateType;

    private _storeID: number;
    private _dispatcherIndex: number;
    private dataCache: {
        [key: string]: IDataCacheRecord;
    };

    constructor(
        public glob: Glob,
        public readonly dispatcher: DispatcherType,
        public readonly actions?: ActionsType,
        public name?: string,
        public doNotSaveState?: boolean
    ) {
        super();

        this._storeID = storeID++;
        this.dataCache = {};
        this._dispatcherIndex = dispatcher.register(this.eventsHandler, this);
    }

    public get storeID() {
        return this._storeID;
    }

    public getState() {
        return this._state;
    }

    public useStoreStateHook<TResult extends StateType>(setStateOnChangingProperties?: TResult) {
        const [storeState, setStoreState] = useState<TResult>({ ...this.getState() } as any);

        useEffect(() => {
            const handleOnStoreChange = () => {
                const newState = { ...this.getState() } as any;

                if (setStateOnChangingProperties != null) {
                    for (let key in setStateOnChangingProperties) {
                        if (newState[key] !== storeState[key]) {
                            setStoreState(newState);
                            break;
                        }
                    }
                } else {
                    setStoreState(newState);
                }
            };

            this.addChangeListener(handleOnStoreChange);

            return () => {
                this.removeChangeListener(handleOnStoreChange);
            };
        }, [storeState]);

        return storeState;
    }

    public useSimpleStoreStateHook<TResult extends StateType>(deps: DependencyList = []) {
        const [storeState, setStoreState] = useState<TResult>({ ...this.getState() } as any);

        useEffect(() => {
            const handleOnStoreChange = () => {
                setStoreState({ ...this.getState() } as TResult);
            };

            this.addChangeListener(handleOnStoreChange);

            return () => {
                this.removeChangeListener(handleOnStoreChange);
            };
        }, deps);

        return storeState;
    }

    public addChangeListener(callback: Function) {
        this.on(BaseStore.CHANGE_EVENT, callback);
    }

    public removeChangeListener(callback: Function) {
        this.off(BaseStore.CHANGE_EVENT, callback);
    }

    public triggerChange(saveState = true) {
        this.trigger(BaseStore.CHANGE_EVENT);

        if (saveState && this.doNotSaveState !== true) {
            this.saveState();
        }
    }

    public emitChange(result?: any, saveState = true) {
        if (result === false) {
            return true;
        }

        if (typeof result === 'object' && typeof result.always === 'function') {
            result.always(() => {
                this.triggerChange(saveState);
            });
        } else {
            this.triggerChange(saveState);
        }

        return true;
    }

    public emitChangeWithoutSaving() {
        this.triggerChange(false);
    }

    public serializeDataToStore(data: StateType): string {
        return null;
    }

    public deserializeStoredData(str: string) {
        let res;

        try {
            res = JSON.parse(str);
        } catch (e) {
            res = {};
        }

        return res;
    }

    public getStorageCacheKey() {
        return STORAGE_STORE_PREFIX + this.name;
    }

    public saveState() {
        if (this.name == null || this.glob.AppStore == null) {
            return;
        }

        const data = this.serializeDataToStore(this._state);

        if (data == null) {
            return;
        }

        const key = this.getStorageCacheKey();

        if (data === '') {
            this.glob.AppStore.removeFromLocalStorage(key);
        } else {
            this.glob.AppStore.saveToLocalStorage(key, data);
        }
    }

    public readSavedState() {
        if (this.name == null || this.glob.AppStore == null) {
            return;
        }

        const key = this.getStorageCacheKey();
        const data = this.glob.AppStore.getFromLocalStorage(key);

        if (data == null) {
            return;
        }

        return this.deserializeStoredData(data);
    }

    public destroy() {
        if (this.dispatcher != null) {
            this.dispatcher.unregister(this._dispatcherIndex);
        }
    }

    public readCache() {
        const constr: any = this.constructor;

        let obj;

        if (typeof constr.getInitialState === 'function') {
            obj = constr.getInitialState();
        } else {
            obj = {};
        }

        this._state = $.extend(true, obj, this.readSavedState());
    }

    public cleanDataCache(key?: string) {
        if (key == null) {
            _.each(this.dataCache, (cache) => {
                this.cleanSelfDestructTimer(cache);
            });

            this.dataCache = {};
        } else {
            this.cleanSelfDestructTimer(this.dataCache[key]);
            delete this.dataCache[key];
        }
    }

    private cleanSelfDestructTimer(cache: IDataCacheRecord) {
        if (cache != null && cache.selfDestructTimer != null) {
            clearTimeout(cache.selfDestructTimer);
            delete cache.selfDestructTimer;
        }
    }

    public isCachedDataLoading(key: string): boolean {
        const cache = this.dataCache[key];

        return cache != null && cache.deferred != null;
    }

    public getCachedData<ValueType>(key: string): ValueType {
        const cache = this.dataCache[key];

        if (cache != null) {
            return cache.value as ValueType;
        }

        return undefined;
    }

    public getCachedDataDeferred<TReturnValue, TRejectValue = null>(
        key: string,
        maxCacheAge: number
    ): JQuery.Deferred<TReturnValue, TRejectValue> {
        const cache = this.dataCache[key];

        if (cache != null) {
            if (cache.value != null) {
                if (maxCacheAge <= 0) {
                    this.cleanSelfDestructTimer(cache);
                    delete this.dataCache[key];

                    return undefined;
                } else {
                    return $.Deferred<TReturnValue>().resolve(cache.value);
                }
            } else {
                return cache.deferred;
            }
        }

        return undefined;
    }

    public putCachedDataDeferred(key: string, deferred: JQuery.Deferred<any>, sqlTimeOutPeriod: number) {
        let cache = this.dataCache[key];

        this.cleanSelfDestructTimer(cache);

        cache = {
            deferred: deferred
        };

        const cacheTimeInSeconds = sqlTimeOutPeriod > 0 ? sqlTimeOutPeriod : DEFAULT_SQL_TIME_OUT_PERIOD;

        cache.selfDestructTimer = setTimeout(() => {
            delete this.dataCache[key];
        }, cacheTimeInSeconds * 1000);

        this.dataCache[key] = cache;
    }

    public putCachedDataValue(key: string, value: any, maxCacheAge: number, minCacheAge: number) {
        let cache = this.dataCache[key];

        this.cleanSelfDestructTimer(cache);

        cache = {
            value: value
        };

        if (minCacheAge == null || minCacheAge < 0) {
            minCacheAge = DEFAULT_MIN_CACHE_AGE;
        }

        const cacheTimeInSeconds = maxCacheAge > minCacheAge ? maxCacheAge : minCacheAge;

        cache.selfDestructTimer = setTimeout(() => {
            delete this.dataCache[key];
        }, cacheTimeInSeconds * 1000);

        this.dataCache[key] = cache;
    }

    public get storeType(): string {
        return this.name;
    }

    protected cleanStateUp() {
        this.dataCache = {};

        const constr: any = this.constructor;

        if (typeof constr.getInitialState === 'function') {
            this._state = constr.getInitialState();
        }

        //console.log(`Store ${this.storeType} - State is cleaned.`);
    }

    protected signOut() {
        this.cleanStateUp();
        //return false;
    }

    protected doNotProcessEvent(actionType: string | number): boolean {
        return false;
    }

    protected eventsHandler(payload: IPayload<any>): boolean {
        //EVG: Warning! The actions below are AppActions. They are catched here if the dispatcher is AppDispatcher.
        // If you need them working in a custom store that is inherited from BaseStore,
        // the contants for custom store's actions should have the same value as of the Constants of AppActions.
        const action = payload.action;

        if (this.doNotProcessEvent(action.actionType)) {
            return false;
        }

        //console.log(`Store: ${this.name ?? this.storeID}, Action: ${action.actionType}`);

        if (checkAction<I.STORE_CACHE_READ>(action, Constants.STORE_CACHE_READ)) {
            if (this.doNotSaveState !== true) {
                this.readCache();
                this.emitChange();
            }
        } else if (checkAction<I.STORE_CACHE_CLEAN>(action, Constants.STORE_CACHE_CLEAN)) {
            if (action.storeID == null || action.storeID === this.storeID) {
                this.cleanStateUp();
            }
        } else if (checkAction<I.SIGN_OUT>(action, Constants.SIGN_OUT)) {
            this.signOut();
        }

        return true;
    }
}
